// src/client/game/ui/LevelCompleteOverlay.ts

import Phaser from "phaser";
import type { Level } from "../core/levels/Level";

type LevelCompleteCallbacks = {
  onNextLevel: () => void;
  onMainMenu: () => void;
};

/**
 * Lightweight overlay shown when a level target score is reached.
 * Keeps UI responsibilities here (scene just calls show/hide + callbacks).
 */
export class LevelCompleteOverlay {
  private container: Phaser.GameObjects.Container;
  private background: Phaser.GameObjects.Rectangle;
  private titleText: Phaser.GameObjects.Text;
  private bodyText: Phaser.GameObjects.Text;

  private nextButton: Phaser.GameObjects.Rectangle;
  private nextLabel: Phaser.GameObjects.Text;
  private menuButton: Phaser.GameObjects.Rectangle;
  private menuLabel: Phaser.GameObjects.Text;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly callbacks: LevelCompleteCallbacks
  ) {
    const { width, height } = scene.scale;

    this.container = scene.add.container(0, 0).setDepth(1000);

    this.background = scene.add.rectangle(
      width / 2,
      height / 2,
      width * 0.82,
      height * 0.62,
      0x000000,
      0.86
    );

    this.titleText = scene.add
      .text(width / 2, height / 2 - height * 0.2, "Level Complete", {
        fontFamily: "monospace",
        fontSize: "30px",
        color: "#ffffff",
      })
      .setOrigin(0.5);

    this.bodyText = scene.add
      .text(width / 2, height / 2 - 10, "", {
        fontFamily: "monospace",
        fontSize: "16px",
        color: "#ffffff",
        align: "center",
        wordWrap: { width: width * 0.68 },
        lineSpacing: 6,
      })
      .setOrigin(0.5);

    const buttonWidth = 240;
    const buttonHeight = 46;
    const buttonSpacing = 14;
    const firstButtonY = height / 2 + 120;

    const makeYellowButton = (
      y: number,
      label: string,
      onClick: () => void
    ): { rect: Phaser.GameObjects.Rectangle; text: Phaser.GameObjects.Text } => {
      const rect = scene.add
        .rectangle(width / 2, y, buttonWidth, buttonHeight, 0xffd700, 1)
        .setStrokeStyle(2, 0x000000)
        .setInteractive({ useHandCursor: true });

      const text = scene.add
        .text(rect.x, rect.y, label, {
          fontFamily: "monospace",
          fontSize: "18px",
          color: "#000000",
        })
        .setOrigin(0.5);

      rect.on("pointerover", () => rect.setFillStyle(0xfff36b, 1));
      rect.on("pointerout", () => rect.setFillStyle(0xffd700, 1));
      rect.on("pointerup", () => {
        this.hide();
        onClick();
      });

      return { rect, text };
    };

    const next = makeYellowButton(
      firstButtonY,
      "Next Level",
      callbacks.onNextLevel
    );
    const menu = makeYellowButton(
      firstButtonY + buttonHeight + buttonSpacing,
      "Main Menu",
      callbacks.onMainMenu
    );

    this.nextButton = next.rect;
    this.nextLabel = next.text;
    this.menuButton = menu.rect;
    this.menuLabel = menu.text;

    this.container.add([
      this.background,
      this.titleText,
      this.bodyText,
      this.nextButton,
      this.nextLabel,
      this.menuButton,
      this.menuLabel,
    ]);

    this.hide();
  }

  show(params: { current: Level; next: Level | null }): void {
    const { current, next } = params;

    if (next) {
      this.titleText.setText(`Level ${current.id} Complete!`);
      this.bodyText.setText(
        `Next: Level ${next.id} – ${next.label}\n\n${next.blurb}\n\nPress SPACE or tap Next Level.`
      );
      this.nextButton.setVisible(true);
      this.nextLabel.setVisible(true);
      this.nextLabel.setText("Next Level");
    } else {
      this.titleText.setText(`Campaign Complete!`);
      this.bodyText.setText(
        `You cleared Level ${current.id}.\n\nPress SPACE or return to the Main Menu.`
      );
      this.nextButton.setVisible(false);
      this.nextLabel.setVisible(false);
    }

    this.container.setVisible(true);
  }

  hide(): void {
    this.container.setVisible(false);
  }

  clear(): void {
    this.container.destroy(true);
  }
}
